import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useYmaps, type YmapsBindings } from '@noor/maps';
import { OrderScooterIcon } from '@noor/ui';
import {
  findLegacyOrderStage,
  type LegacyCourierTracking,
  type LegacyPoint,
} from '@/entities/legacy-order';
import type { LegacyRoutePath } from '@/entities/legacy-order-draft';
import {
  legacyNextPoint,
  legacyShortDate,
  legacyShortTime,
  legacyShowsTargetPlacemark,
  type LegacyRoutePoint,
} from '../lib/order-detail';

/**
 * The order's route on a map — a READ-ONLY reproduction of the old panel's
 * `OrderMiniMap` (and, when `isExpanded`, the map body of its fullscreen
 * `MapShow` mode; the fullscreen HEADER lives in the drawer).
 *
 * Deliberately its own component rather than a reuse of
 * `legacy-order-create`'s map: that one exists to EDIT a draft (draggable
 * pins, click-to-geocode, a locate-me button, a floating form panel to reserve
 * space for), none of which applies to a placed order. FSD also forbids one
 * widget importing another, so sharing it would mean pushing an editing-aware
 * component down into `entities` for no benefit.
 *
 * Pickups are numbered orange, drop-offs green, matching the manifest badges.
 * The route polyline and the courier tracking are computed ABOVE this
 * component (`useLegacyOrderRoute`) so they survive the remounts below.
 */

/** Zoom used when the whole route collapses to a single point. */
const SINGLE_POINT_ZOOM = 16;
/** Padding as a fraction of the route's span, so pins aren't flush to the edge. */
const BOUNDS_PADDING = 0.35;

interface LegacyOrderMiniMapProps {
  points: LegacyRoutePoint[];
  /** The order's stage — the card header's title and update-line gate. */
  stage: number;
  /** Live courier fix, if any — drawn as the scooter/target placemarks. */
  tracking: LegacyCourierTracking | null;
  /** The road-snapped polyline fetched by `useLegacyOrderRoute`. */
  route: LegacyRoutePath | null;
  /** The courier-first routed points (camera framing), or null. */
  courierPath: LegacyPoint[] | null;
  /**
   * Expanded over the whole drawer body. The mini card's whole HEADER is the
   * expand click target, exactly as the source's `OrderMiniMap` header was.
   */
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  className?: string;
}

/**
 * Gate + chrome. The SDK is loaded asynchronously and may be disabled entirely
 * (no API key), so the canvas only mounts once the bindings exist.
 *
 * Card chrome is the source's `mapContainer`: radius 24, a 4px solid white
 * border, white background. When NO stop has coordinates the canvas is not
 * rendered at all (`OrderMiniMap.tsx:191`) — the card stays an empty shell.
 */
export function LegacyOrderMiniMap({
  points,
  stage,
  tracking,
  route,
  courierPath,
  isExpanded = false,
  onToggleExpand,
  className = '',
}: LegacyOrderMiniMapProps) {
  const { t } = useTranslation('legacy');
  const { bindings, isDisabled } = useYmaps();

  /*
   * The source sized its canvas with a ResizeObserver on the card and REKEYED
   * the map box on every height change (`MapShow`'s `key={height}_box`) —
   * ymaps sizes its viewport when the map mounts and does not re-measure when
   * the container grows, so without the remount a stretched card kept a
   * stale-sized tile with the camera framed for the old box. Reproduced here:
   * the measured height joins the canvas key, so growing the info column (or
   * toggling fullscreen) re-measures and re-fits. Cheap, because the map holds
   * no user state (read-only, no panning to preserve).
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeight(Math.round(entry.contentRect.height));
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  /** Only points with real coordinates — a 0/0 is "not set", not null island. */
  const placed = useMemo(
    () => points.filter((p) => p.location?.lat && p.location.long),
    [points],
  );

  const chrome = `relative overflow-hidden rounded-[24px] border-4 border-[var(--lg-surface)] bg-[var(--lg-surface)] ${className}`;

  if (isDisabled || !bindings) {
    return <div className={chrome} />;
  }

  const knownStage = findLegacyOrderStage(stage);
  const now = new Date();

  return (
    <div ref={containerRef} className={chrome}>
      {placed.length > 0 && (
        <MiniMapCanvas
          key={`${isExpanded ? 'expanded' : 'inline'}-${height}`}
          bindings={bindings}
          points={points}
          placed={placed}
          stage={stage}
          tracking={tracking}
          route={route}
          courierPath={courierPath}
        />
      )}

      {/*
        The mini card's header — INSIDE the card, floating over the canvas in
        the source's white pill (`MapShow`'s name box: 8px side insets, 10px
        top, 76px tall, radius 23, shadow). The WHOLE header is the expand
        target, as the source's `headerBox` click was.
      */}
      {!isExpanded && placed.length > 0 && onToggleExpand && (
        <button
          type="button"
          onClick={onToggleExpand}
          aria-label={t('single.expand_map')}
          className="absolute top-[10px] right-2 left-2 z-10 flex h-[76px] cursor-pointer items-center justify-between rounded-[23px] bg-[var(--lg-surface)] px-[15px] py-[10px] text-left shadow-[var(--lg-card-shadow)]"
        >
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-[22px] font-semibold text-[var(--lg-fg)]">
              {knownStage ? t(knownStage.labelKey) : stage}
            </span>
            {/* Only while the order is still moving — closed stages carry no
                "last updated" line (`OrderMiniMap.tsx:165`). */}
            {stage <= 13 && (
              <span className="text-[16px] font-normal text-[var(--lg-grey-600)]">
                {`${t('single.update')} ${legacyShortDate(now)} ${legacyShortTime(now)}`}
              </span>
            )}
          </span>
          <span className="flex size-14 shrink-0 items-center justify-center rounded-full border border-[var(--lg-grey-250)]">
            <ChevronRightIcon
              className="size-5 text-[var(--lg-orange-500)]"
              aria-hidden="true"
            />
          </span>
        </button>
      )}
    </div>
  );
}

function MiniMapCanvas({
  bindings,
  points,
  placed,
  stage,
  tracking,
  route,
  courierPath,
}: {
  bindings: YmapsBindings;
  /** ALL legs — the next-point search must see un-located ones too. */
  points: LegacyRoutePoint[];
  placed: LegacyRoutePoint[];
  stage: number;
  tracking: LegacyCourierTracking | null;
  route: LegacyRoutePath | null;
  courierPath: LegacyPoint[] | null;
}) {
  const nextPoint = useMemo(() => legacyNextPoint(points), [points]);
  const target = tracking?.target_destination ?? null;
  const courier = tracking?.location ?? null;

  /**
   * Camera framing. When a courier route is drawn the camera frames THAT
   * (courier → target → next stop), not the full stop list — the source's
   * `MapShow` bounds preferred `customPoints` the same way.
   */
  const framed: LegacyPoint[] = useMemo(
    () =>
      courierPath ??
      placed.map((p) => p.location).filter((l): l is LegacyPoint => l !== null),
    [courierPath, placed],
  );

  const location = useMemo(() => {
    const first = framed[0];
    if (!first || framed.length === 1) {
      return {
        center: [first?.long ?? 0, first?.lat ?? 0] as [number, number],
        zoom: SINGLE_POINT_ZOOM,
      };
    }

    const longs = framed.map((p) => p.long);
    const lats = framed.map((p) => p.lat);
    const minLong = Math.min(...longs);
    const maxLong = Math.max(...longs);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const padLong = Math.max((maxLong - minLong) * BOUNDS_PADDING, 0.002);
    const padLat = Math.max((maxLat - minLat) * BOUNDS_PADDING, 0.002);

    return {
      bounds: [
        [minLong - padLong, maxLat + padLat],
        [maxLong + padLong, minLat - padLat],
      ] as [[number, number], [number, number]],
    };
  }, [framed]);

  /*
   * MEMOIZED. `reactify.module` mints fresh component identities on every
   * call, so calling it inline re-created `YMap` each render — React saw a
   * different component type, unmounted the old map and mounted a new one, and
   * the freshly fitted camera was thrown away with it. The symptom is a map
   * stuck at its default region-wide zoom with every marker collapsed onto one
   * pixel, which reads as "bounds are being ignored" rather than as a remount.
   */
  const {
    YMap,
    YMapDefaultSchemeLayer,
    YMapDefaultFeaturesLayer,
    YMapMarker,
    YMapFeature,
  } = useMemo(
    () => bindings.reactify.module(bindings.ymaps3),
    [bindings.reactify, bindings.ymaps3],
  );

  const drawableRoute = route && route.length > 1 && framed.length > 1 ? route : null;

  return (
    <div className="absolute inset-0">
      {/* The 100px top margin keeps the fitted route clear of the floating
          header pill, as the source's `AdaptiveYMaps` margin did. */}
      <YMap
        location={location}
        mode="vector"
        margin={[100, 0, 0, 0]}
        className="size-full"
      >
        <YMapDefaultSchemeLayer />
        <YMapDefaultFeaturesLayer />

        {drawableRoute && (
          <YMapFeature
            geometry={{ type: 'LineString', coordinates: drawableRoute }}
            style={{
              stroke: [{ color: 'var(--lg-orange-500)', width: 4 }],
              fill: 'transparent',
            }}
          />
        )}

        {placed.map((point, index) => (
          <YMapMarker
            key={point.id ?? index}
            coordinates={[point.location?.long ?? 0, point.location?.lat ?? 0]}
          >
            <span
              className="flex size-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-[var(--lg-surface)] text-[12px] font-semibold text-[var(--lg-surface)] shadow-md"
              style={{
                backgroundColor:
                  point.type === 'origin'
                    ? 'var(--lg-orange-500)'
                    : 'var(--lg-green-500)',
              }}
            >
              {index + 1}
            </span>
          </YMapMarker>
        ))}

        {/*
          The dispatcher's target, when it differs from the order's own next
          stop (a chained pickup): the source's 40px black circle with the
          white scooter glyph (`OrderMiniMap.tsx:120-138`).
        */}
        {legacyShowsTargetPlacemark({ target, nextPoint }) && target && (
          <YMapMarker coordinates={[target.long, target.lat]}>
            <ScooterBadge className="bg-[var(--lg-fg)]" />
          </YMapMarker>
        )}

        {/*
          The courier's live position while the order is in flight. The source
          drew its `driver.png` raster here, rotated along the fetched path;
          that asset is not portable, so the same scooter glyph stands in on a
          brand-orange badge — a STATIC glyph, the rotation-along-path is not
          reproduced.

          No offer-message placemark: the old drawer also drew the websocket
          offer courier here, but the socket rejects merchant tokens
          server-side, so the offer stream never delivers and the placemark is
          deliberately not ported.
        */}
        {courier && stage !== 15 && (
          <YMapMarker coordinates={[courier.long, courier.lat]}>
            <ScooterBadge className="bg-[var(--lg-orange-500)]" />
          </YMapMarker>
        )}
      </YMap>
    </div>
  );
}

/** A bottom-anchored courier badge using the shared scooter icon. */
function ScooterBadge({ className }: { className: string }) {
  return (
    <span
      className={`flex size-10 -translate-x-1/2 -translate-y-full items-center justify-center rounded-full border-2 border-[var(--lg-surface)] ${className}`}
    >
      <OrderScooterIcon className="size-6 text-[var(--lg-surface)]" />
    </span>
  );
}
